/**
 * Activity addition simulator serializer (EPIC-027 / 27.5)
 *
 * Reads the values from a `data-controller="activity-addition-form"` root and
 * produces the camelCase JSON body expected by
 * `POST /simulator/add-activity/calculate` (EPIC-027 / 27.3, §17.B).
 *
 * Distinct from `simulator_serializer.js` (§17.A) — schema diverges:
 *   - `existingActivities[]` keyed by `activityId` with optional growth surcharge.
 *   - `newActivity{}` with `projectedStartDate` + `projectedCa12m`.
 *   - `horizonMonths` ∈ {12, 24} (no 36 in §17.B).
 *   - No ARE/ARCE allocation in this simulator.
 */

function parseNumber(raw, { defaultValue = 0, allowNull = false } = {}) {
  if (raw === null || raw === undefined) {
    return allowNull ? null : defaultValue;
  }
  const cleaned = String(raw).replace(/[\s  ]/g, "").replace(",", ".").trim();
  if (cleaned === "") {
    return allowNull ? null : defaultValue;
  }
  const n = Number.parseFloat(cleaned);
  if (Number.isNaN(n)) {
    return allowNull ? null : defaultValue;
  }
  return n;
}

function parseInteger(raw, { defaultValue = 0, allowNull = false } = {}) {
  const n = parseNumber(raw, { defaultValue: NaN, allowNull: true });
  if (n === null || Number.isNaN(n)) {
    return allowNull ? null : defaultValue;
  }
  return Math.trunc(n);
}

function pressedHorizon(root) {
  const btn = root.querySelector('[data-horizon][aria-pressed="true"], [data-horizon].nm-btn--solid');
  const raw = btn?.dataset?.horizon;
  const n = Number.parseInt(raw ?? "12", 10);
  return n === 24 ? 24 : 12;
}

function activeCaMode(root) {
  const btn = root.querySelector(
    '[data-input-mode][aria-pressed="true"], [data-input-mode].nm-btn--solid',
  );
  return btn?.dataset?.inputMode === "monthly" ? "monthly" : "linear";
}

function sumMonthlyCa(root) {
  const inputs = root.querySelectorAll('[data-monthly-ca-input]');
  let total = 0;
  inputs.forEach((el) => {
    total += parseNumber(el.value, { defaultValue: 0 });
  });
  return total;
}

function serializeExistingActivity(card) {
  const activityId = card.dataset.activityId ?? null;
  const regime = card.dataset.regime ?? "bic_services";
  const description = card.dataset.description ?? "";
  const realCa12m = parseNumber(card.dataset.realCa12mEur, { defaultValue: 0 });
  const growthInput = card.querySelector('[data-slot="growthInput"]');
  const growthRaw = growthInput?.value ?? "";
  const evolutionRateSurcharge =
    growthRaw === ""
      ? null
      : parseNumber(growthRaw, { defaultValue: 0 }) / 100; // % → ratio décimal

  return {
    activityId,
    regime,
    description,
    realCa12m,
    evolutionRateSurcharge,
  };
}

function serializeNewActivity(root) {
  const regime = root.querySelector("#new-category")?.value ?? "bic_services";
  const description = root.querySelector("#new-description")?.value?.trim() ?? "";
  const nafCode = root.querySelector("#new-naf")?.value?.trim() || null;
  const location = root.querySelector("#new-place")?.value?.trim() || null;
  const projectedStartDate = root.querySelector("#new-start-date")?.value || null;

  const regulatedRaw = root.querySelector("#new-regulated")?.value ?? "none";
  const regulatedCategory =
    !regulatedRaw || regulatedRaw === "none" ? null : regulatedRaw;

  const rcProInput = root.querySelector('[data-field="rcProAnnual"]');
  const rcProAnnual = parseNumber(rcProInput?.value, { defaultValue: null, allowNull: true });

  const caMode = activeCaMode(root);
  const projectedCa12m =
    caMode === "monthly"
      ? sumMonthlyCa(root)
      : parseNumber(root.querySelector("#new-ca-12m")?.value, { defaultValue: 0 });

  return {
    regime,
    description,
    projectedStartDate,
    projectedCa12m,
    nafCode,
    location,
    regulatedCategory,
    rcProAnnual,
  };
}

function serializeGlobalOptions(root) {
  const vlOption = root.querySelector('[data-field="vlOption"]')?.value === "true";
  const tmiRaw = root.querySelector('[data-field="tmi"]')?.value;
  const tmiAllowed = new Set([0, 11, 30, 41, 45]);
  const tmiParsed = parseInteger(tmiRaw, { defaultValue: 11 });
  const tmi = vlOption ? null : (tmiAllowed.has(tmiParsed) ? tmiParsed : 11);

  const fiscalShares = parseInteger(root.querySelector('[data-field="fiscalShares"]')?.value, {
    defaultValue: 1,
  });
  const referenceFiscalIncomeN2 = parseNumber(
    root.querySelector('[data-field="referenceFiscalIncomeN2"]')?.value,
    { defaultValue: 0 },
  );

  const acreEligible =
    root.querySelector('[data-field="acreEligible"]')?.value === "true";
  const acreStartDate = root.querySelector('[data-field="acreStartDate"]')?.value || null;
  const acreEndDate = root.querySelector('[data-field="acreEndDate"]')?.value || null;

  return {
    vlOption,
    tmi,
    referenceFiscalIncomeN2,
    fiscalShares,
    acreEligible,
    acreStartDate,
    acreEndDate,
  };
}

export function serializeActivityAdditionForm(root) {
  const existingActivities = Array.from(
    root.querySelectorAll('[data-activity-role="existing"]'),
  ).map(serializeExistingActivity);

  return {
    simulationDate: root.querySelector('[data-field="simulationDate"]')?.value || null,
    horizonMonths: pressedHorizon(root),
    existingActivities,
    newActivity: serializeNewActivity(root),
    globalOptions: serializeGlobalOptions(root),
  };
}

/**
 * Restores a previously-serialized state into the DOM. Only the fields that
 * exist in `root` are touched; missing fields are ignored.
 */
export function hydrateActivityAdditionForm(root, state) {
  if (!state || typeof state !== "object") return;

  if (Array.isArray(state.existingActivities)) {
    state.existingActivities.forEach((row) => {
      if (!row?.activityId) return;
      const card = root.querySelector(
        `[data-activity-role="existing"][data-activity-id="${cssEscape(row.activityId)}"]`,
      );
      if (!card) return;
      const growthInput = card.querySelector('[data-slot="growthInput"]');
      if (growthInput && row.evolutionRateSurcharge !== null && row.evolutionRateSurcharge !== undefined) {
        growthInput.value = String(Math.round(row.evolutionRateSurcharge * 1000) / 10);
      }
    });
  }

  if (state.newActivity && typeof state.newActivity === "object") {
    const setVal = (selector, value) => {
      const el = root.querySelector(selector);
      if (el && value !== undefined && value !== null) el.value = value;
    };
    setVal("#new-category", state.newActivity.regime);
    setVal("#new-description", state.newActivity.description);
    setVal("#new-naf", state.newActivity.nafCode);
    setVal("#new-place", state.newActivity.location);
    setVal("#new-start-date", state.newActivity.projectedStartDate);
    setVal("#new-ca-12m", state.newActivity.projectedCa12m);
    setVal(
      "#new-regulated",
      state.newActivity.regulatedCategory === null ? "none" : state.newActivity.regulatedCategory,
    );
  }

  if (state.globalOptions && typeof state.globalOptions === "object") {
    const setField = (name, value) => {
      const el = root.querySelector(`[data-field="${name}"]`);
      if (el && value !== undefined && value !== null) el.value = value;
    };
    setField("vlOption", state.globalOptions.vlOption ? "true" : "false");
    setField("tmi", state.globalOptions.tmi);
    setField("fiscalShares", state.globalOptions.fiscalShares);
    setField("referenceFiscalIncomeN2", state.globalOptions.referenceFiscalIncomeN2);
    setField("acreEligible", state.globalOptions.acreEligible ? "true" : "false");
    setField("acreStartDate", state.globalOptions.acreStartDate);
    setField("acreEndDate", state.globalOptions.acreEndDate);
  }
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(value);
  }
  return String(value).replace(/([!"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g, "\\$1");
}
